import React, { useState } from 'react';
import brainImg from '../../images/creative-idea-icon.svg'
import ThemeButton from 'components/NewHome/ThemeButton';
import { DialogDetails } from './MasonryGrid';
import { analyzeImageWithGemini, GeminiParams } from 'api/mediaApi';

interface DialogProps {
    onClose: () => void;
    modelDetails: DialogDetails
}

const FeedDialog: React.FC<DialogProps> = ({ onClose, modelDetails }) => {
    const { modelType, title, imgId, url, imgType, name, isInvalid } = modelDetails;
    const [selectedPromptIndex, setSelectedPromptIndex] = useState<Number | null>(null);
    const [aiResponse, setAiResponse] = useState('');

    const geminiPromptsList = [
        'Identify the product in the media',
        'Analyze the recall risk for this product',
        'Assess the cyber vulnerabilities of this product',
        'Explain sustainability or circularity concerns'
    ];

    const selectGeminiPrompt = async (index: number) => {
        setSelectedPromptIndex(index);
        const sendToGemini: GeminiParams = {
            id: imgId,
            url: url,
            name: name,
            type: imgType,
            prompt: geminiPromptsList[index]
        };

        const result = await analyzeImageWithGemini(sendToGemini);
        if (result && result?.success) {
            setAiResponse(result?.analysis?.candidates[0].content.parts[0].text)
        };
    }

    const handleClose = () => {
        setAiResponse('');
        onClose();
    };

    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 text-center transition-opacity duration-300 opacity-100`}>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-gray-500/75 transition-opacity"
                aria-hidden="true"
                onClick={handleClose}
            ></div>

            {/* Dialog */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="dialog-title"
                className={`
                    relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300
                    w-auto  max-w-4xl md:max-h-[75%] max-h-[60%] overflow-y-auto mx-auto my-6
                  `}
            >
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">

                        <div className="mt-3 mx-auto">
                            <h3 className="text-center md:text-xl text-base font-semibold text-[#373940] mb-3" id="dialog-title">
                                {aiResponse ? `${modelType} generated response` : isInvalid ? 'Warning' : title}
                            </h3>
                            <div className="border-t pt-2">
                                {isInvalid ? (
                                    <p className="text-base text-primaryColor">
                                        This image can't be analyzed as it's unavailable or invalid. Please try again with a different one.
                                    </p>
                                ) : (
                                    aiResponse ? (
                                        <p className="md:text-base text-xs leading-5">{aiResponse}</p>
                                    ) : modelType === 'gemini' ? (
                                        <div className="mt-2 grid lg:grid-cols-4 grid-cols-2 justify-center gap-3">
                                            {geminiPromptsList.map((prompt, index) => {
                                                const isActive = selectedPromptIndex === index;
                                                return (
                                                    <div
                                                        key={index}
                                                        onClick={() => selectGeminiPrompt(index)}
                                                        className={`sm:p-3 p-2 border-2 rounded-md sm:w-[200px] w-auto min-h-[130px] cursor-pointer
                                                        ${isActive ? 'shadow-lg border-primaryColor bg-[#ECCACA]' : 'border-gray-300 bg-lightGrayBgColor'}`}
                                                    >
                                                        <div className="flex items-center justify-center mb-2">
                                                            <img
                                                                className={`md:w-20 w-12 ${isActive ? '' : 'opacity-50'}`}
                                                                src={brainImg}
                                                                alt="brain"
                                                            />
                                                        </div>
                                                        <p className={`md:text-base text-xs text-center ${isActive ? '' : 'text-gray-400'}`}>
                                                            {prompt}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* dialog bottom */}
                <div className="bg-white px-4 py-3 flex items-center justify-end sm:px-6 gap-2">
                    <ThemeButton
                        bgColor="bg-white"
                        textColor="text-[#373940]"
                        padding="p-3"
                        size="text-sm font-semibold"
                        rounded="rounded-md"
                        border="border"
                        className={`inline-flex ${(modelType === 'vertex' && !isInvalid) ? 'w-1/2' : 'w-auto'} justify-center shadow-xs hover:bg-gray-50`}
                        onClick={handleClose}
                    >
                        Cancel
                    </ThemeButton>
                    {(modelType === 'vertex' && !isInvalid) &&
                        <ThemeButton
                            bgColor="bg-primaryColor"
                            textColor="text-white"
                            padding="p-3"
                            size="text-sm font-semibold"
                            rounded="rounded-md"
                            className="inline-flex w-1/2 justify-center shadow-xs"
                            onClick={handleClose}
                        >
                            Yes, Send
                        </ThemeButton>
                    }
                </div>
            </div>
        </div>
    );
};

export default FeedDialog;